import { PermissionSchema } from '@/types/admin-management/admin-roles-types';

// Note: Permissions and roles are now fetched from the backend API
// No hardcoded values - all data comes from:
// - GET /admin/roles (for roles)
// - GET /admin/role-permissions?role_id=xxx (for permissions)

/**
 * Centralized function to check if user is a super admin
 * Checks multiple possible role indicators from backend
 */
export const isSuperAdmin = (userProfile, userRoles = []) => {
    // Method 1: Profile Role (Basic) - from login response
    if (userProfile?.role) {
        const role = userProfile.role.toLowerCase();
        if (role === 'super_admin' || role === 'super administrator' || role === 'superadmin') {
            return true;
        }
    }

    // Method 1: Profile is_super flag
    if (userProfile?.is_super === true) {
        return true;
    }

    // Method 2: Detailed Roles (Comprehensive) - from detailed roles API
    if (Array.isArray(userRoles)) {
        return userRoles.some(role => {
            const roleName = role.role_name?.toLowerCase() || '';
            const isSuper = role.is_super === true;
            
            return roleName === 'super_admin' || 
                   roleName === 'super administrator' || 
                   roleName === 'superadmin' ||
                   isSuper;
        });
    }

    return false;
};

/**
 * Centralized function to check if user is an admin (including super admin)
 */
export const isAdmin = (userProfile, userRoles = []) => {
    // Super admin is also an admin
    if (isSuperAdmin(userProfile, userRoles)) {
        return true;
    }

    // Check userProfile role field
    if (userProfile?.role) {
        const role = userProfile.role.toLowerCase();
        if (role.includes('admin')) {
            return true;
        }
    }

    // Check userRoles array
    if (Array.isArray(userRoles)) {
        return userRoles.some(role => {
            const roleName = role.role_name?.toLowerCase() || '';
            return roleName.includes('admin');
        });
    }

    return false;
};

/**
 * Check if user has a specific permission using backend permission data
 * @param permissionsList - Array of permissions from backend API
 * @param permissionName - Name of permission to check
 * @returns boolean - true if user has permission
 */
export const hasPermission = (permissionsList: Array<PermissionSchema>, permissionName: string): boolean => {
    if (!Array.isArray(permissionsList)) return false;
    return permissionsList.some(permission => permission.name === permissionName);
};

/**
 * Get permissions for a specific role (deprecated - use API directly)
 * @deprecated Use useGetRolePermissions hook instead
 */
export const getRolePermissions = (role: string): string[] => {
    console.warn('getRolePermissions is deprecated. Use useGetRolePermissions hook instead.');
    return [];
};

/**
 * Get all available permissions (deprecated - use API directly)
 * @deprecated Use useGetAllPermissions hook instead
 */
export const getAllPermissions = (): string[] => {
    console.warn('getAllPermissions is deprecated. Use useGetAllPermissions hook instead.');
    return [];
};

/**
 * Check if user has access to a specific permission
 * @param permissionsList - Array of permissions from backend API
 * @param permissionName - Name of permission to check
 * @returns boolean - true if user has permission
 */
export const doesUserHaveAccess = (
    permissionsList: Array<PermissionSchema>,
    permissionName: string
): boolean => {
    if (!Array.isArray(permissionsList)) return false;
    return permissionsList.some(
        (permission) => permission.name === permissionName
    );
};
